import { billetStatus } from "@/shared/selectors/BilletStatusSelector";
import { TimeService } from "@dexteel/mesf-core";
import { Visibility } from "@mui/icons-material";
import { Box, Divider, IconButton, Tooltip } from "@mui/material";
import moment from "moment-timezone";
import { Fragment, useState } from "react";
import {
	computeMonthSummary,
	DayCell,
	DayCellItem,
	daysInMonth,
	formatTons,
	formatTph,
	formatYield,
	getMonthStatus,
	MONTHS_LABELS,
	MonthStatus,
	MonthSummary,
	safeRatio,
} from "./utils/yearGrid";

const formatRange = (start: Date, end: Date, tz: string): string =>
	`${moment.tz(start, tz).format("MMM D, HH:mm")} → ${moment
		.tz(end, tz)
		.format("MMM D, HH:mm")}`;

const renderItemTooltip = (
	item: Omit<DayCellItem, "color"> | DayCellItem,
	tz: string,
) => {
	if (item.eventTypeCode === "P") {
		const isPlanned = item.statusCode === "P";
		const tons = isPlanned
			? item.scheduledFinishedTons
			: item.actualFinishedTons;
		const label = isPlanned ? "Scheduled" : "Actual";
		const value =
			tons === null || tons === undefined
				? "—"
				: `${parseFloat(`${tons}`).toLocaleString("en-US")} tons`;
		const secondaryLabel = item.familyName;
		return (
			<Box>
				<Box sx={{ fontWeight: 600, color: "#f8fafc" }}>
					{item.displayName}
					{secondaryLabel && (
						<Box
							component="span"
							sx={{ color: "#f8fafc", fontWeight: 400, ml: 0.75 }}
						>
							| {secondaryLabel}
						</Box>
					)}
				</Box>
				<Box
					sx={{
						color: "#f8fafc",
						fontSize: 11,
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{formatRange(item.start, item.end, tz)}
				</Box>
				<Box
					sx={{
						color: "#f8fafc",
						fontSize: 11,
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{label}: {value}
				</Box>
			</Box>
		);
	}
	return (
		<Box>
			<Box sx={{ fontWeight: 600, color: "#f8fafc" }}>{item.fullName}</Box>
			<Box
				sx={{
					color: "#94a3b8",
					fontSize: 11,
					fontVariantNumeric: "tabular-nums",
				}}
			>
				{formatRange(item.start, item.end, tz)}
			</Box>
		</Box>
	);
};

const tooltipSlotProps = {
	tooltip: {
		sx: {
			fontSize: 12,
			padding: "10px 12px",
			borderRadius: 1.5,
			boxShadow: "0 10px 30px -10px rgba(15, 23, 42, 0.5)",
			maxWidth: 320,
		},
	},
};

const headerCellSx = {
	backgroundColor: "#f8fafc",
	textAlign: "center",
	fontWeight: 500,
	fontSize: 11,
	color: "#64748b",
	letterSpacing: "0.04em",
	py: 1,
	fontVariantNumeric: "tabular-nums",
};

const monthLabelSx = {
	backgroundColor: "#f8fafc",
	fontWeight: 600,
	fontSize: 11,
	color: "#0f172a",
	display: "flex",
	alignItems: "center",
	justifyContent: "flex-start",
	px: 1.5,
	transition: "background-color 0.15s ease, color 0.15s ease",
};

const dayCellSx = {
	backgroundColor: "#ffffff",
	display: "flex",
	flexDirection: "column",
	alignItems: "stretch",
	justifyContent: "stretch",
	padding: 0,
	overflow: "hidden",
	position: "relative",
	transition: "box-shadow 0.15s ease",
	"&:hover": {
		zIndex: 3,
		boxShadow: `0 0 0 2px "#2563eb", 0 8px 24px -6px rgba(15, 23, 42, 0.18)`,
	},
	userSelect: "none",
};

const summaryRowSx = {
	display: "flex",
	justifyContent: "space-between",
	gap: 2,
	color: "#f8fafc",
	fontSize: 11,
	fontVariantNumeric: "tabular-nums",
};

const summaryHeaderSx = {
	color: "#f8fafc",
	fontSize: 10,
	fontWeight: "bold",
	textTransform: "uppercase" as const,
	letterSpacing: "0.05em",
	mt: 0.75,
	mb: 0.25,
};

const ROW_HEIGHT = 55;

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
	<Box sx={summaryRowSx}>
		<Box component="span">{label}</Box>
		<Box component="span" sx={{ fontWeight: 600 }}>
			{value}
		</Box>
	</Box>
);

const renderMonthTooltip = (summary: MonthSummary, status: MonthStatus) => {
	const actualTph = safeRatio(
		summary.actualRolledTons,
		summary.actualRollingHours,
	);
	const actualYield = safeRatio(
		summary.actualFinishedTons,
		summary.actualChargedTons,
	);
	const budgetTph = safeRatio(
		summary.scheduledRolledTons,
		summary.scheduledRollingHours,
	);
	const scheduledYield = safeRatio(
		summary.scheduledFinishedTons,
		summary.scheduledChargedTons,
	);

	const showActual = status === "past" || status === "current";
	const showScheduled = status === "future" || status === "current";
	const actualLabel = status === "current" ? "MTD Actual" : "Actual";

	return (
		<Box sx={{ minWidth: 200 }}>
			<Box
				sx={{
					color: "#f8fafc",
					fontSize: 12,
					fontWeight: 700,
					letterSpacing: "0.02em",
					pb: 0.5,
					borderBottom: "1px solid rgba(248, 250, 252, 0.15)",
				}}
			>
				Month Summary
			</Box>
			{showActual && (
				<>
					<Box sx={summaryHeaderSx}>{actualLabel}</Box>
					<SummaryRow
						label="Finished Tons"
						value={formatTons(summary.actualFinishedTons)}
					/>
					<SummaryRow label="TPH" value={formatTph(actualTph)} />
					<SummaryRow label="Finished Yield" value={formatYield(actualYield)} />
				</>
			)}
			{showActual && showScheduled && (
				<Divider sx={{ marginTop: 1, backgroundColor: "#ffffff31" }} />
			)}
			{showScheduled && (
				<>
					<Box sx={summaryHeaderSx}>Scheduled</Box>
					<SummaryRow
						label="Finished Tons"
						value={formatTons(summary.scheduledFinishedTons)}
					/>
					<SummaryRow label="TPH" value={formatTph(budgetTph)} />
					<SummaryRow
						label="Finished Yield"
						value={formatYield(scheduledYield)}
					/>
				</>
			)}
		</Box>
	);
};

export const CalendarYearTable = ({
	year,
	grid,
	goToMonth,
	onEventClick,
	billetStatusMode = false,
}: {
	year: number;
	grid: DayCell[][];
	goToMonth: (monthIndex: number, eventId?: number) => void;
	onEventClick: (eventId: number) => void;
	billetStatusMode?: boolean;
}) => {
	const [hoveredEventId, setHoveredEventId] = useState<number | null>(null);
	const tz = TimeService.getInstance().getServerTimeZone();

	const today = moment.tz(tz);
	const isCurrentYear = today.year() === year;
	const todayYear = today.year();
	const todayMonth = today.month();
	const todayDate = today.date();

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: "110px repeat(31, minmax(36px, 1fr))",
				gap: "1px",
				backgroundColor: "rgba(15, 23, 42, 0.06)",
				minWidth: 1200,
				borderRadius: 2,
				overflow: "hidden",
				border: `1px solid rgba(15, 23, 42, 0.06)`,
				boxShadow:
					"0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px -8px rgba(15, 23, 42, 0.08)",
			}}
		>
			<Box sx={headerCellSx} />
			{Array.from({ length: 31 }, (_, i) => (
				<Box key={i} sx={headerCellSx}>
					{i + 1}
				</Box>
			))}

			{MONTHS_LABELS.map((monthName, monthIdx) => {
				const dim = daysInMonth(year, monthIdx);
				const monthStatus = getMonthStatus(
					year,
					monthIdx,
					todayYear,
					todayMonth,
				);
				const monthSummary = computeMonthSummary(grid[monthIdx]);
				return (
					<Fragment key={`row-${monthIdx}`}>
						<Tooltip
							arrow
							slotProps={tooltipSlotProps}
							title={renderMonthTooltip(monthSummary, monthStatus)}
						>
							<Box
								key={`m-${monthIdx}`}
								sx={{
									...monthLabelSx,
									minHeight: ROW_HEIGHT,
									cursor: "pointer",
									"&:hover": {
										backgroundColor: "rgba(37, 99, 235, 0.08)",
										color: "#2563eb",
									},
								}}
								onClick={() => goToMonth(monthIdx)}
							>
								{monthName}
							</Box>
						</Tooltip>
						{Array.from({ length: 31 }, (_, dayIdx) => {
							const isOutOfMonth = dayIdx + 1 > dim;
							if (isOutOfMonth) {
								return (
									<Box
										key={`${monthIdx}-${dayIdx}`}
										sx={{
											minHeight: ROW_HEIGHT,
											background:
												"repeating-linear-gradient(135deg, #f8fafc, #f8fafc 6px, #eef2f7 6px, #eef2f7 7px)",
										}}
									/>
								);
							}
							const cell = grid[monthIdx][dayIdx];
							const isToday =
								isCurrentYear &&
								monthIdx === todayMonth &&
								dayIdx + 1 === todayDate;
							return (
								<Box
									key={`${monthIdx}-${dayIdx}`}
									sx={{
										...dayCellSx,
										minHeight: ROW_HEIGHT,
										...(isToday && {
											outline: 2,
											outlineColor: "primary.main",
											outlineOffset: -2,
											zIndex: 2,
										}),
									}}
								>
									{cell.sections.map((s, idx) => {
										const next = cell.sections[idx + 1];
										const showDivider = next != null && next.key !== s.key;
										const bsMatch =
											billetStatusMode &&
											s.eventTypeCode === "P" &&
											s.billetStatusCode
												? billetStatus.find(
														(b) => b.code === s.billetStatusCode,
													)
												: null;
										const resolvedColor = bsMatch
											? bsMatch.backgroundColor
											: s.color;
										const isHovered = hoveredEventId === s.eventId;
										const hoverStyle = isHovered
											? {
													filter: "brightness(1.08)",
													boxShadow: `inset 0 0 0 1.5px color-mix(in srgb, ${resolvedColor} 70%, #000)`,
												}
											: {};
										return (
											<Tooltip
												key={`${s.key}-${idx}`}
												arrow
												slotProps={tooltipSlotProps}
												title={renderItemTooltip(s, tz)}
											>
												<Box
													onMouseEnter={() => setHoveredEventId(s.eventId)}
													onMouseLeave={() => setHoveredEventId(null)}
													onClick={(e) => {
														e.stopPropagation();
														if (e.ctrlKey || e.metaKey) {
															goToMonth(monthIdx, s.eventId);
														} else {
															onEventClick(s.eventId);
														}
													}}
													sx={{
														flex: 1,
														minHeight: 0,
														fontSize: 11,
														lineHeight: 1.2,
														textAlign: "center",
														fontWeight: 600,
														letterSpacing: "0.01em",
														width: "100%",
														backgroundColor: resolvedColor,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														px: 0.5,
														cursor: "pointer",
														transition:
															"filter 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
														...(showDivider && {
															borderBottom: "1px solid #f8fafc",
														}),
														...hoverStyle,
													}}
												>
													{s.displayName}
												</Box>
											</Tooltip>
										);
									})}
									{cell.overflowSections.length > 0 && (
										<Tooltip
											arrow
											slotProps={tooltipSlotProps}
											title={
												<Box>
													{cell.overflowSections.map((i) => (
														<Box
															key={i.key}
															sx={{
																mb: 0.75,
																display: "flex",
																alignItems: "center",
																gap: 1,
															}}
														>
															<Box sx={{ flex: 1, minWidth: 0 }}>
																{renderItemTooltip(i, tz)}
															</Box>
															<Tooltip title="See event on calendar">
																<IconButton
																	size="small"
																	onClick={(e) => {
																		e.stopPropagation();
																		goToMonth(monthIdx, i.eventId);
																	}}
																	sx={{
																		color: "#f8fafc",
																		"&:hover": {
																			backgroundColor:
																				"rgba(248, 250, 252, 0.15)",
																		},
																	}}
																>
																	<Visibility sx={{ fontSize: "0.8rem" }} />
																</IconButton>
															</Tooltip>
														</Box>
													))}
												</Box>
											}
										>
											<Box
												sx={{
													fontSize: 9,
													fontWeight: 600,
													color: "#64748b",
													textAlign: "center",
													py: 0.25,
													backgroundColor: "rgba(15, 23, 42, 0.04)",
													transition: "background-color 0.15s ease",
													...(hoveredEventId !== null &&
														cell.overflowSections.some(
															(o) => o.eventId === hoveredEventId,
														) && {
															backgroundColor: "rgba(15, 23, 42, 0.08)",
														}),
												}}
											>
												+{cell.overflowSections.length}
											</Box>
										</Tooltip>
									)}
								</Box>
							);
						})}
					</Fragment>
				);
			})}
		</Box>
	);
};
